import React, { useState } from 'react';
import { auth } from '../services/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useTheme } from '../ThemeContext';

function LoginScreen({ onLogin }) {
  const { t } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please enter your credentials');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../services/firebase');
      const adminDoc = await getDoc(firestoreDoc(db, 'admins', userCredential.user.uid));
      
      if (!adminDoc.exists()) {
        setError('This account is not authorized as an admin');
        await auth.signOut();
        setLoading(false);
        return;
      }

      const adminData = adminDoc.data();
      onLogin({
        email: userCredential.user.email,
        uid: userCredential.user.uid,
        school: adminData.schoolName,
        schoolId: adminData.schoolId,
        role: adminData.role || 'Administrator'
      });
    } catch (err) {
      console.log('LOGIN ERROR:', err.code, err.message);
      setError('Invalid email or password');
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: t.bgGradient,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Background circles */}
      <div style={{
        position: 'absolute', top: '-120px', left: '-80px',
        width: 360, height: 360, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,125,216,0.06) 0%, transparent 70%)',
      }} />
      <div style={{
        position: 'absolute', bottom: '-100px', right: '-60px',
        width: 300, height: 300, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,125,216,0.04) 0%, transparent 70%)',
      }} />
      {/* Floating dots */}
      <div style={{ position: 'absolute', top: '18%', left: '12%', width: 8, height: 8, borderRadius: '50%', background: 'rgba(59,125,216,0.15)' }} />
      <div style={{ position: 'absolute', top: '55%', right: '15%', width: 6, height: 6, borderRadius: '50%', background: 'rgba(59,125,216,0.12)' }} />
      <div style={{ position: 'absolute', bottom: '25%', left: '20%', width: 10, height: 10, borderRadius: '50%', background: 'rgba(59,125,216,0.08)' }} />

      <div style={{
        background: t.card,
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderRadius: 24, padding: '48px 40px', width: 400, maxWidth: '90vw',
        boxShadow: '0 8px 40px rgba(59,125,216,0.08), 0 1px 3px rgba(0,0,0,0.04)',
        border: '1px solid rgba(59,125,216,0.08)',
        textAlign: 'center', position: 'relative', zIndex: 1,
      }}>
        {/* PS Logo */}
        <img 
          src="/logo.png" 
          alt="PeerStudy" 
          style={{
            width: 72, height: 72, borderRadius: 16,
            margin: '0 auto 16px', display: 'block',
            boxShadow: '0 4px 16px rgba(59,125,216,0.25)',
          }}
        />

        <h1 style={{ fontSize: 26, fontWeight: 700, color: t.text, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
          PeerStudy
        </h1>
        <p style={{ fontSize: 14, color: '#8E8E93', margin: '0 0 32px', fontWeight: 500 }}>
          Admin Portal
        </p>

        {error && (
          <div style={{
            background: '#FFF2F2', border: '1px solid #FFD4D4', borderRadius: 12,
            padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#D63031', fontWeight: 500,
          }}>
            {error}
          </div>
        )}

        <div style={{ textAlign: 'left', marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'block' }}>
            Email
          </label>
          <div style={{
            display: 'flex', alignItems: 'center', background: t.input, borderRadius: 14, padding: '0 16px',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M22 4L12 13L2 4" />
            </svg>
            <input
              type="email"
              placeholder="admin@university.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                flex: 1, border: 'none', background: 'none', padding: '14px 12px',
                fontSize: 15, outline: 'none', color: t.text, fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ textAlign: 'left', marginBottom: 28 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'block' }}>
            Password
          </label>
          <div style={{
            display: 'flex', alignItems: 'center', background: t.input, borderRadius: 14, padding: '0 16px',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              style={{
                flex: 1, border: 'none', background: 'none', padding: '14px 12px',
                fontSize: 15, outline: 'none', color: t.text, fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <button
          onClick={handleLogin}
          disabled={loading}
          style={{
            width: '100%', padding: 15, borderRadius: 14, border: 'none',
            background: loading ? '#8EAFD4' : 'linear-gradient(135deg, #3B7DD8 0%, #2E6BC4 100%)',
            color: '#fff', fontSize: 16, fontWeight: 600, cursor: loading ? 'default' : 'pointer',
            fontFamily: 'inherit', letterSpacing: '0.2px',
            boxShadow: '0 4px 16px rgba(59,125,216,0.3)',
          }}
        >
          {loading ? 'Signing in...' : 'Sign In →'}
        </button>

        <p style={{ fontSize: 12, color: '#C7C7CC', marginTop: 20 }}>
          Admin accounts are created by PeerStudy.
        </p>
      </div>
    </div>
  );
}

export default LoginScreen;